import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  Tooltip,
  Stack,
} from '@mui/material';
import RouterIcon from '@mui/icons-material/Router';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import InfoIcon from '@mui/icons-material/Info';
import { formatDeviceType } from '../../utils/deviceTypes';

export const RegistrationCard = ({ registration, formatDateTime, onDetails }) => {
  const requestType = registration.request_type || registration.registration_type;
  const isNewDevice = requestType === 'new_device';
  const isOwnershipTransfer = requestType === 'ownership_transfer';
  const isDeviceReplacement = requestType === 'device_replacement';
  
  const macAddress = isNewDevice 
    ? registration.requested_mac_addr 
    : (registration.device_detail?.mac_addr || 'N/A');

  return (
    <Card
      variant="outlined"
      sx={{
        '&:hover': {
          boxShadow: 2,
          borderColor: 'primary.main',
        },
        transition: 'all 0.2s ease-in-out',
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <RouterIcon fontSize="small" color="action" />
              <Typography variant="subtitle1" fontWeight="600">
                {macAddress}
              </Typography>
              <Chip
                label={registration.request_type_display || registration.registration_type_display || (registration.request_type === 'tailscale_approval' || registration.registration_type === 'tailscale_approval' ? 'Tailscale Approval' : 'Device Registration')}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ ml: 1 }}
              />
              {(registration.status || registration.status_display) && (() => {
                const status = (registration.status || '').toLowerCase();
                let chipColor = 'default';
                if (status === 'pending') {
                  chipColor = 'warning';
                } else if (status === 'approved' || status === 'completed') {
                  chipColor = 'success';
                } else if (status === 'rejected' || status === 'denied' || status === 'cancelled') {
                  chipColor = 'error';
                }
                return (
                  <Chip
                    label={registration.status_display || registration.status}
                    size="small"
                    color={chipColor}
                    sx={{ ml: 1 }}
                  />
                );
              })()}
            </Box>
            <Stack direction="row" spacing={3} flexWrap="wrap" sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PersonIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  <strong>Requested by:</strong>{' '}
                  {registration.requested_by_detail?.display_name 
                    ? (registration.requested_by_detail.email 
                        ? `${registration.requested_by_detail.display_name} (${registration.requested_by_detail.email})`
                        : registration.requested_by_detail.display_name)
                    : (registration.requested_by_detail?.email || 'N/A')}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CalendarTodayIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  <strong>Date:</strong> {registration.requested_at || registration.requested_date ? formatDateTime(registration.requested_at || registration.requested_date, 'YYYY-MM-DD HH:mm:ss') : 'N/A'}
                </Typography>
              </Box>

              {!isNewDevice && registration.device_detail?.mac_addr && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <RouterIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    <strong>Device MAC:</strong> {registration.device_detail.mac_addr}
                  </Typography>
                </Box>
              )}

              {(registration.requested_device_type || registration.device_type) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Device Type:</strong> {formatDeviceType(registration.requested_device_type || registration.device_type)}
                  </Typography>
                </Box>
              )}

              {(registration.requested_pi || registration.pi) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>PI:</strong> {registration.requested_pi || registration.pi}
                  </Typography>
                </Box>
              )}

              {registration.expiry_date && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <AccessTimeIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    <strong>Expires:</strong> {formatDateTime(registration.expiry_date, 'YYYY-MM-DD HH:mm:ss')}
                  </Typography>
                </Box>
              )}

              {isOwnershipTransfer && registration.requested_to_detail && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PersonIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    <strong>Transfer To:</strong> {registration.requested_to_detail.display_name || registration.requested_to_detail.email || 'N/A'}
                  </Typography>
                  {registration.requested_to_detail.email && registration.requested_to_detail.display_name && (
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                      ({registration.requested_to_detail.email})
                    </Typography>
                  )}
                </Box>
              )}

              {isDeviceReplacement && registration.replacement_device_detail && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <RouterIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    <strong>Replacement Device:</strong> {registration.replacement_device_detail.mac_addr || 'N/A'}
                  </Typography>
                </Box>
              )}
            </Stack>
            {registration.reason && (
              <Box sx={{ mt: 1.5, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  <strong>Reason:</strong> {registration.reason}
                </Typography>
              </Box>
            )}
            {!registration.reason && registration.notes && (
              <Box sx={{ mt: 1.5, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  <strong>Notes:</strong> {registration.notes}
                </Typography>
              </Box>
            )}
            {(registration.reviewed_by_detail || registration.approved_by_detail) && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  <strong>Reviewed By:</strong> {(registration.reviewed_by_detail || registration.approved_by_detail).display_name || (registration.reviewed_by_detail || registration.approved_by_detail).email}
                </Typography>
              </Box>
            )}
            {(registration.reviewed_at || registration.approved_date) && (
              <Box sx={{ mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  <strong>Reviewed Date:</strong> {formatDateTime(registration.reviewed_at || registration.approved_date, 'YYYY-MM-DD HH:mm:ss')}
                </Typography>
              </Box>
            )}
            {registration.completed_at && (
              <Box sx={{ mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  <strong>Completed At:</strong> {formatDateTime(registration.completed_at, 'YYYY-MM-DD HH:mm:ss')}
                </Typography>
              </Box>
            )}
            {registration.rejection_reason && (
              <Box sx={{ 
                mt: 1, 
                p: 1.5, 
                bgcolor: 'rgba(211, 47, 47, 0.1)', 
                border: '1px solid rgba(211, 47, 47, 0.3)',
                borderRadius: 1 
              }}>
                <Typography variant="body2" sx={{ color: '#c62828', fontWeight: 500 }}>
                  <strong>Rejection Reason:</strong> {registration.rejection_reason}
                </Typography>
              </Box>
            )}
          </Box>
          {onDetails && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <Tooltip title="View Details">
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  startIcon={<InfoIcon />}
                  onClick={() => onDetails(registration)}
                  sx={{ minWidth: 100 }}
                >
                  Details
                </Button>
              </Tooltip>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

